import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from '@phosphor-icons/react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import SEO from '../components/SEO';
import { SectionHero, SectionHeading, StatsRow, CTABanner } from '../components/SiteBlocks';
import { Button } from '../components/ui/button';
import { PRICING_MODELS } from '../data/content';

export default function PricingPage() {
  return (
    <div className="min-h-screen" style={{ background: 'var(--background)', color: 'var(--text-primary)' }}>
      <SEO path="/pricing" title="Pricing — Engagement Models" description="XceedAgents pricing: MVP Sprint from $25K, Offshore Development Center from $8.5K/engineer/month, Staff Augmentation from $45/hr, Managed Services custom-priced." />
      <Navbar />

      <SectionHero
        eyebrow="Pricing"
        title="Transparent, outcome-based pricing."
        subtitle="Choose the engagement model that fits your team and timeline. All include senior engineers + 8 specialist AI agents. All include 100% code ownership."
      >
        <Link to="/start-project"><Button className="h-12 px-7 font-semibold hover-lift gap-2" style={{ background: 'var(--primary-accent)', color: '#fff' }} data-testid="pricing-cta">Request for Proposal <ArrowRight size={16} weight="bold" /></Button></Link>
      </SectionHero>

      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PRICING_MODELS.map((m) => (
              <div
                key={m.id}
                className={`p-7 rounded-2xl border ${m.featured ? 'bg-white' : 'bg-white'} relative`}
                style={{
                  borderColor: m.featured ? 'var(--primary-accent)' : 'var(--border)',
                  borderWidth: m.featured ? 2 : 1,
                }}
                data-testid={`pricing-${m.id}`}
              >
                {m.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-xs font-bold tracking-wider uppercase rounded-full" style={{ background: 'var(--primary-accent)', color: '#fff' }}>Most Popular</div>
                )}
                <h3 className="font-heading text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>{m.name}</h3>
                <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>{m.description}</p>
                <div className="font-heading text-3xl font-bold mb-1" style={{ color: 'var(--primary-accent)' }}>{m.price}</div>
                <div className="text-xs mb-6" style={{ color: 'var(--text-muted)' }}>{m.duration}</div>
                <ul className="space-y-2 mb-6">
                  {m.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-primary)' }}>
                      <CheckCircle weight="fill" size={16} className="mt-0.5 flex-shrink-0" style={{ color: 'var(--primary-accent)' }} />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to="/start-project">
                  <Button className="w-full font-semibold" style={{ background: m.featured ? 'var(--primary-accent)' : 'var(--text-primary)', color: '#fff' }}>
                    {m.cta} →
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <StatsRow stats={[
        { value: '500+', label: 'Projects Delivered' },
        { value: '60%', label: 'Cost Savings vs Onshore' },
        { value: '100%', label: 'Code Ownership' },
        { value: '4-6 wk', label: 'Typical MVP Timeline' },
      ]} />

      <section className="py-20" style={{ background: 'var(--surface)' }}>
        <div className="max-w-4xl mx-auto px-6">
          <SectionHeading eyebrow="What's always included" title="Every engagement, every model." align="center" />
          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {[
              'Senior engineers only (5+ yrs experience)',
              '8 specialist AI agents per engineer',
              '100% code ownership — yours from day 1',
              'NDA-ready, SOC 2 / GDPR / HIPAA compatible',
              'Weekly demos + transparent project tracking',
              'Direct Slack/Teams channel with team',
              'Free 2-week warranty post-delivery',
              'Quarterly business reviews',
            ].map((f, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-lg bg-white border" style={{ borderColor: 'var(--border)' }}>
                <CheckCircle weight="fill" size={18} className="mt-0.5 flex-shrink-0" style={{ color: 'var(--primary-accent)' }} />
                <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABanner title="Not sure which model fits?" subtitle="Tell our AI about your project. We'll recommend the right engagement model with a free proposal in 60 seconds." ctaText="Request for Proposal" ctaHref="/start-project" />
      <Footer />
    </div>
  );
}
